// productKey.js
// Reversible 16-digit product key from a MAC address using a decimal-domain
// Feistel network whose round function is AES-ECB (via crypto-js).

import CryptoJS from "crypto-js";

const BASE = 10 ** 8; // we split 16 digits into two 8-digit halves
const N_ROUNDS = 10; // 8–12 recommended

// ───────────────────────── MAC ↔ int (48-bit) ─────────────────────────
const macToInt = (macStr) => {
    console.log("MAC STR: ",macStr);
    
  const s = (macStr || "").replace(/[^0-9a-fA-F]/g, "");
  if (s.length !== 12 || !/^[0-9a-fA-F]{12}$/.test(s)) {
    throw new Error("MAC must have 12 hex chars (e.g., AA:BB:CC:DD:EE:FF)");
  }
  // BigInt is supported in modern RN & browsers; stay in decimal later anyway.
  return BigInt("0x" + s);
};

const intToMac = (macInt) => {
  let h = macInt.toString(16).padStart(12, "0").toUpperCase();
  return h.match(/.{1,2}/g).join(":");
};

// ───────────────────────── crypto-js helpers ─────────────────────────
const toWordArray16 = (bytesLike) => {
  // Build a 16-byte block from string/Uint8Array; right-pad with zeros or truncate
  let u8;
  if (bytesLike == null) {
    u8 = new Uint8Array(0);
  } else if (typeof bytesLike === "string") {
    const wa = CryptoJS.enc.Utf8.parse(bytesLike);
    u8 = wordArrayToUint8Array(wa);
  } else if (bytesLike instanceof Uint8Array) {
    u8 = bytesLike;
  } else if (Array.isArray(bytesLike)) {
    u8 = Uint8Array.from(bytesLike);
  } else {
    throw new Error("Unsupported bytesLike input");
  }
  if (u8.length < 16) {
    const out = new Uint8Array(16);
    out.set(u8, 0);
    u8 = out;
  } else if (u8.length > 16) {
    u8 = u8.subarray(0, 16);
  }
  return CryptoJS.lib.WordArray.create(u8);
};

const keyToWordArray = (key) => {
  // Accepts string or Uint8Array of length 16/24/32 (AES-128/192/256)
  if (typeof key === "string") {
    const wa = CryptoJS.enc.Utf8.parse(key);
    const len = byteLenOfWordArray(wa);
    if (![16, 24, 32].includes(len)) {
      throw new Error("String key must be 16/24/32 bytes when UTF-8 encoded.");
    }
    return wa;
  } else if (key instanceof Uint8Array) {
    if (![16, 24, 32].includes(key.length)) throw new Error("Uint8Array key must be 16, 24, or 32 bytes.");
    return CryptoJS.lib.WordArray.create(key);
  } else {
    throw new Error("Key must be a string or Uint8Array.");
  }
};

const wordArrayToUint8Array = (wordArray) => {
  const { words, sigBytes } = wordArray;
  const u8 = new Uint8Array(sigBytes);
  let i = 0;
  let j = 0;
  while (i < sigBytes) {
    const w = words[(i / 4) | 0];
    u8[i++] = (w >> 24) & 0xff;
    if (i >= sigBytes) break;
    u8[i++] = (w >> 16) & 0xff;
    if (i >= sigBytes) break;
    u8[i++] = (w >> 8) & 0xff;
    if (i >= sigBytes) break;
    u8[i++] = w & 0xff;
  }
  return u8;
};

const byteLenOfWordArray = (wa) => {
  return wa.sigBytes;
};

// AES-ECB single-block PRF: PRF(key, data16) -> 16 bytes
const aesEcbEncryptBlock = (key, data16) => {
  const keyWA = keyToWordArray(key);
  const dataWA = toWordArray16(data16);
  const enc = CryptoJS.AES.encrypt(dataWA, keyWA, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.NoPadding,
  });
  // CryptoJS returns base64 ciphertext; convert back to bytes
  const ctWA = enc.ciphertext;
  return wordArrayToUint8Array(ctWA); // 16 bytes
};

// ───────────────────────── Round function ─────────────────────────
const roundFunction = (key, roundCounter, rightInt, tweak) => {
  const rightStr = String(rightInt).padStart(8, "0"); // 8 ASCII digits
  const tweakBytes =
    tweak == null
      ? new Uint8Array(0)
      : typeof tweak === "string"
      ? wordArrayToUint8Array(CryptoJS.enc.Utf8.parse(tweak))
      : tweak instanceof Uint8Array
      ? tweak
      : wordArrayToUint8Array(CryptoJS.enc.Utf8.parse(String(tweak)));

  // Build 1-byte round || 8-byte ASCII || (fill rest with tweak)
  const base = new Uint8Array(1 + 8 + tweakBytes.length);
  base[0] = roundCounter & 0xff;
  for (let i = 0; i < 8; i++) base[1 + i] = rightStr.charCodeAt(i);
  base.set(tweakBytes, 1 + 8);

  const block16 = aesEcbEncryptBlock(key, base); // 16 bytes
  // Convert bytes -> big integer mod 10^8 -> Number
  let bi = 0n;
  for (let i = 0; i < block16.length; i++) {
    bi = (bi << 8n) | BigInt(block16[i]);
  }
  const mod = Number(bi % 100000000n); // 10^8
  return mod;
};

// ───────────────────────── Feistel over 16-digit decimal ─────────────────────────
const feistelEncrypt16Digits = (plaintext16, key, { tweak = null, rounds = N_ROUNDS } = {}) => {
  if (!/^\d{16}$/.test(plaintext16)) throw new Error("Plaintext must be 16 digits.");
  let L = parseInt(plaintext16.slice(0, 8), 10);
  let R = parseInt(plaintext16.slice(8), 10);

  for (let r = 0; r < rounds; r++) {
    const F = roundFunction(key, r, R, tweak);
    L = (L + F) % BASE;
    [L, R] = [R, L]; // swap
  }
  return String(L).padStart(8, "0") + String(R).padStart(8, "0");
};

const feistelDecrypt16Digits = (ciphertext16, key, { tweak = null, rounds = N_ROUNDS } = {}) => {
  if (!/^\d{16}$/.test(ciphertext16)) throw new Error("Ciphertext must be 16 digits.");
  let L = parseInt(ciphertext16.slice(0, 8), 10);
  let R = parseInt(ciphertext16.slice(8), 10);

  for (let r = rounds - 1; r >= 0; r--) {
    [L, R] = [R, L]; // reverse swap
    const F = roundFunction(key, r, R, tweak);
    L = (L - F) % BASE;
    if (L < 0) L += BASE;
  }
  return String(L).padStart(8, "0") + String(R).padStart(8, "0");
};

// ───────────────────────── High-level API ─────────────────────────
// We pack MAC (48-bit) into 15 decimal digits with a leading version digit.
export const macToProductKey = (macStr, key, { versionDigit = "0", tweak = null, rounds = N_ROUNDS } = {}) => {
  if (!/^[0-9]$/.test(versionDigit)) throw new Error("versionDigit must be one decimal digit.");
  const macInt = macToInt(macStr); // 0 .. 2^48-1
  const mac15 = macInt.toString(10).padStart(15, "0");
  const plaintext16 = versionDigit + mac15; // 16 digits
  return feistelEncrypt16Digits(plaintext16, key, { tweak, rounds });
};

export const productKeyToMac = (productKey, key, { expectedVersionDigit = "0", tweak = null, rounds = N_ROUNDS } = {}) => {
  if (!/^\d{16}$/.test(productKey)) throw new Error("Product key must be 16 digits.");
  const plaintext16 = feistelDecrypt16Digits(productKey, key, {
    tweak,
    rounds,
  });
  const v = plaintext16[0];
  if (v !== expectedVersionDigit) throw new Error("Version mismatch (or wrong key/tweak).");
  const mac15 = plaintext16.slice(1);
  const macInt = BigInt(mac15);
  return intToMac(macInt);
};


